import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  captureEvent,
  getFirstTouchAttribution,
  identifyAuthenticatedUser,
  resetPostHogUser,
} from '../lib/analytics/posthog';
import { formatRegistrationError } from '../lib/formatApiError';
import { buildLoginUrlWithReturn } from '../lib/navigation';
import { isInviteSignPath } from '../lib/signLink';

interface User {
  id: string;
  email: string;
  name: string;
  surname: string;
  phone?: string;
  fin?: string;
}

interface RegisterResult {
  message: string;
  /** True when the API returned a token (immediate session); otherwise user must confirm email / log in */
  loggedIn: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<RegisterResult>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (email: string, token: string, newPassword: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  updateUser: (userData: Partial<User>) => Promise<void>;
  fetchProfile: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  surname: string;
  phone: string;
  fin: string;
  organizationId?: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const UNAUTHORIZED_EVENT_NAME = 'auth:unauthorized';

/** Resource-level 401s on signing flows must not invalidate the user session. */
const RESOURCE_AUTH_SKIP_PATHS = [
  '/api/Document/GetDocument?',
  '/api/Document/UpdateStatus',
  '/api/SignProcessor/',
  '/api/Document/GetFile/',
  '/api/sign-link/',
] as const;

const getFetchRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
};

/** Public signing link: `/document?sid=…` without `did` — may be opened while logged in as another user. */
const isPublicSignerLinkRoute = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname !== '/document') return false;
  const params = new URLSearchParams(window.location.search);
  return Boolean(params.get('sid')?.trim()) && !params.get('did')?.trim();
};

const shouldSkipGlobalUnauthorizedLogout = (requestUrl: string): boolean =>
  RESOURCE_AUTH_SKIP_PATHS.some((path) => requestUrl.includes(path)) ||
  isPublicSignerLinkRoute();

let fetchInterceptorInstalled = false;

const installFetchInterceptor = () => {
  if (typeof window === 'undefined' || fetchInterceptorInstalled) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const response = await originalFetch(...args);

    if (response.status === 401) {
      const requestUrl = getFetchRequestUrl(args[0]);
      if (!shouldSkipGlobalUnauthorizedLogout(requestUrl)) {
        window.dispatchEvent(new Event(UNAUTHORIZED_EVENT_NAME));
      }
    }

    return response;
  };

  fetchInterceptorInstalled = true;
};

installFetchInterceptor();

const getAuthStorage = (): Storage => localStorage;

const migrateSessionAuthToLocal = () => {
  const sessionToken = sessionStorage.getItem('authToken');
  const sessionUser = sessionStorage.getItem('authUser');
  if (sessionToken && sessionUser && !localStorage.getItem('authToken')) {
    localStorage.setItem('authToken', sessionToken);
    localStorage.setItem('authUser', sessionUser);
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authUser');
  }
};

const mapProfileResponse = (data: Record<string, unknown>, fallback?: Partial<User>): User => ({
  id: data.id != null ? String(data.id) : fallback?.id ?? '',
  email: typeof data.email === 'string' ? data.email : fallback?.email ?? '',
  name: typeof data.name === 'string' ? data.name : fallback?.name ?? '',
  surname: typeof data.surname === 'string' ? data.surname : fallback?.surname ?? '',
  phone: typeof data.phone === 'string' ? data.phone : fallback?.phone,
  fin: typeof data.fin === 'string' ? data.fin : fallback?.fin,
});

const fetchProfileWithToken = async (authToken: string, fallback?: Partial<User>): Promise<User> => {
  const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/User/Profile`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to fetch profile' }));
    throw new Error(errorData.message || 'Failed to fetch profile');
  }

  const data = (await response.json()) as Record<string, unknown>;
  return mapProfileResponse(data, fallback);
};

const persistUser = (userData: User) => {
  getAuthStorage().setItem('authUser', JSON.stringify(userData));
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user and token from storage on mount, then refresh profile from API
  useEffect(() => {
    const loadStoredAuth = async () => {
      migrateSessionAuthToLocal();
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('authUser');

      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser) as User;
        setToken(storedToken);
        setUser(parsedUser);

        try {
          const profileData = await fetchProfileWithToken(storedToken, parsedUser);
          setUser(profileData);
          persistUser(profileData);
        } catch (error) {
          console.error('Failed to refresh profile on load:', error);
        }
      }

      setIsLoading(false);
    };

    loadStoredAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
       const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/User/Authenticate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Login failed' }));
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();
      const authToken = data.token || data.accessToken;
      const userData: User = {
        id: data.user?.id || data.id,
        email: data.user?.email || email,
        name: data.user?.name || data.name,
        surname: data.user?.surname || data.surname,
        phone: data.user?.phone || data.phone,
        fin: data.user?.fin || data.fin,
      };

      setToken(authToken);
      setUser(userData);

      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('authUser');
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');

      localStorage.setItem('authToken', authToken);
      localStorage.setItem('authUser', JSON.stringify(userData));

      try {
        const profileData = await fetchProfileWithToken(authToken, userData);
        setUser(profileData);
        localStorage.setItem('authUser', JSON.stringify(profileData));
        identifyAuthenticatedUser(profileData.id);
      } catch (profileError) {
        console.error('Failed to load profile after login:', profileError);
        identifyAuthenticatedUser(userData.id);
      }

      captureEvent('user_logged_in', getFirstTouchAttribution());
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (userData: RegisterData): Promise<RegisterResult> => {
    try {
      const envOrgId = Number(process.env.REACT_APP_ORGANIZATION_ID);
      const organizationId =
        userData.organizationId ??
        (Number.isFinite(envOrgId) && envOrgId > 0 ? envOrgId : 1);

      const emailTrimmed = userData.email.trim();
      const payload = {
        username: emailTrimmed,
        email: emailTrimmed,
        password: userData.password,
        name: userData.name,
        surname: userData.surname,
        phone: userData.phone,
        fin: userData.fin,
        organizationId,
      };

      const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/User/Register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

      if (!response.ok) {
        throw new Error(formatRegistrationError(data));
      }

      const message =
        typeof data.message === 'string' && data.message.trim()
          ? data.message.trim()
          : 'User registered successfully. Please check your email to confirm your account.';

      const authToken = data.token ?? data.accessToken;
      const hasToken = typeof authToken === 'string' && authToken.length > 0;
      if (hasToken) {
        const fromUser =
          data.user && typeof data.user === 'object' && data.user !== null
            ? (data.user as { id?: unknown })
            : undefined;
        const rawId = fromUser?.id ?? data.id;
        const newUser: User = {
          id: rawId !== undefined && rawId !== null ? String(rawId) : '',
          email: emailTrimmed,
          name: userData.name,
          surname: userData.surname,
          phone: userData.phone,
          fin: userData.fin,
        };

        setToken(authToken);
        setUser(newUser);
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('authUser');
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('authUser', JSON.stringify(newUser));

        try {
          const profileData = await fetchProfileWithToken(authToken, newUser);
          setUser(profileData);
          localStorage.setItem('authUser', JSON.stringify(profileData));
          identifyAuthenticatedUser(profileData.id);
        } catch (profileError) {
          console.error('Failed to load profile after registration:', profileError);
          identifyAuthenticatedUser(newUser.id);
        }

        captureEvent('user_registered', {
          logged_in: true,
          ...getFirstTouchAttribution(),
        });
      } else {
        captureEvent('user_registered', {
          logged_in: false,
          ...getFirstTouchAttribution(),
        });
      }

      return { message, loggedIn: hasToken };
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const requestPasswordReset = async (email: string) => {
    const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/User/RequestPasswordReset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Password reset request failed' }));
      throw new Error(errorData.message || errorData.title || 'Password reset request failed');
    }
  };

  const resetPassword = async (email: string, token: string, newPassword: string) => {
    const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/User/ResetPassword`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        token,
        NewPassword: newPassword,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Password reset failed' }));
      throw new Error(errorData.message || errorData.title || 'Password reset failed');
    }
  };

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    // Clear both localStorage and sessionStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authUser');
    resetPostHogUser();
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
      if (isInviteSignPath(window.location.pathname)) return;
      if (window.location.pathname !== '/login') {
        const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.replace(buildLoginUrlWithReturn(returnPath));
      }
    };

    window.addEventListener(UNAUTHORIZED_EVENT_NAME, handleUnauthorized);

    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT_NAME, handleUnauthorized);
    };
  }, [logout]);

  const fetchProfile = useCallback(async () => {
    try {
      if (!token) {
        throw new Error('Not authenticated');
      }

      const profileData = await fetchProfileWithToken(token);
      setUser(profileData);
      persistUser(profileData);
    } catch (error) {
      console.error('Fetch profile error:', error);
      throw error;
    }
  }, [token]);

  const updateUser = async (userData: Partial<User>) => {
    try {
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/User/Profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Update failed' }));
        throw new Error(errorData.message || 'Update failed');
      }

      const data = await response.json();
      const updatedUser: User = {
        ...user!,
        ...userData,
        ...(data.user || data),
      };

      setUser(updatedUser);
      persistUser(updatedUser);
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    requestPasswordReset,
    resetPassword,
    logout,
    isAuthenticated: !!user && !!token,
    isLoading,
    updateUser,
    fetchProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

